// Story repository: the only place the UI's story reads go through.
// Every Firestore query is bounded, filters on status == "published" (required
// by the security rules — rules are not filters), and uses one-shot reads,
// never real-time listeners. It uses Firestore Lite (no listeners, no offline
// cache): every read goes to the server, so an outage surfaces as an error
// instead of a misleading empty cached result, and the bundle is smaller.
import {
  Timestamp,
  collection,
  doc,
  documentId,
  getCount,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentSnapshot,
  type Firestore,
  type QueryConstraint,
} from "firebase/firestore/lite";
import { LIMITS, type Story } from "../../shared/model.ts";
import { storyFromData } from "../../shared/storyCodec.ts";
import { buildPlatformMap, type PlatformMap } from "../../shared/platformMap.ts";
import { SEARCH_INDEX_MAX_SHARDS, buildSearchIndex, type SearchEntry, type SearchIndexDoc } from "../../shared/searchIndex.ts";
import { buildDigest, type DigestDoc } from "../../shared/digest.ts";
import { buildRoundups, type RoundupDoc } from "../../shared/roundup.ts";
import type { TrendsDoc } from "../../shared/trends.ts";
import { sampleTrends } from "../../shared/sampleTrends.ts";
import type { ServerFilter } from "./filters.ts";
import { storyRoles } from "../../shared/roles.ts";

export type Cursor = unknown;

export interface StoryPage {
  stories: Story[];
  cursor: Cursor | null;
  hasMore: boolean;
}

export interface StoryRepository {
  readonly label: string;
  listPage(filter: ServerFilter, since: number | null, cursor: Cursor | null): Promise<StoryPage>;
  /** Server-side count for the same query (count aggregation: ~1 read per 1,000 matches). */
  countPublished(filter: ServerFilter, since: number | null): Promise<number>;
  /** Fetches published stories by ID in bounded batches. Missing/unpublished IDs are omitted. */
  getByIds(ids: string[]): Promise<Map<string, Story>>;
  /** The precomputed Platform map (one document read). Null if not generated yet. */
  getPlatformMap(): Promise<PlatformMap | null>;
  /** Published stories tagged with a product, newest first, paged. */
  listByProduct(productId: string, cursor: Cursor | null): Promise<StoryPage>;
  /** Published stories tagged with ANY of up to 30 products (array-contains-any), newest first. */
  /** Newest first; `before` (ms) starts the page at an older date, e.g. a timeline week. */
  listByProducts(productIds: string[], cursor: Cursor | null, before?: number): Promise<StoryPage>;
  /** The precomputed archive search index (one document read). Null if not generated yet. */
  getSearchIndex(): Promise<SearchIndexDoc | null>;
  /** The precomputed Digest (one document read). Null if not generated yet. */
  getDigest(): Promise<DigestDoc | null>;
  /** The precomputed weekly roundups (one document read). */
  getRoundups(): Promise<RoundupDoc | null>;
  /** Precomputed reader trends from anonymous click counts (one document read). Null if not built yet. */
  getTrends(): Promise<TrendsDoc | null>;
}

const IN_QUERY_MAX = 30;
/** Timeline "Show all" pages (31 reads each, including the has-more probe). */
export const PRODUCT_PAGE_SIZE = 30;

export function chunk<T>(list: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/** Turns SDK errors into messages a reader (or the site owner) can act on. */
export function friendlyError(err: unknown, unreachableMessage: string): Error {
  const code = (err as { code?: string })?.code ?? "";
  if (code === "permission-denied")
    return new Error("Access was denied. If you run this site, check that firestore.rules are deployed and queries filter on published stories.");
  if (code === "failed-precondition")
    return new Error("A required Firestore index is missing or still building. Deploy firestore.indexes.json and wait for it to finish.");
  if (code === "resource-exhausted") return new Error("The site has reached its database quota for now. Please try again later.");
  return new Error(unreachableMessage);
}

function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(message)), ms);
    p.then(
      (v) => (clearTimeout(t), resolve(v)),
      (e) => (clearTimeout(t), reject(friendlyError(e, message))),
    );
  });
}

export class FirestoreStoryRepository implements StoryRepository {
  readonly label = "Firestore";
  private readonly db: Firestore;
  private readonly timeoutMessage: string;
  private readonly pageSize: number;

  constructor(db: Firestore, timeoutMessage: string, pageSize: number = LIMITS.pageSize) {
    this.db = db;
    this.timeoutMessage = timeoutMessage;
    this.pageSize = pageSize;
  }

  private baseConstraints(filter: ServerFilter, since: number | null): QueryConstraint[] {
    const constraints: QueryConstraint[] = [where("status", "==", "published")];
    if (filter.kind === "topic") constraints.push(where("primaryTopic", "==", filter.topic));
    if (filter.kind === "audience") constraints.push(where("audiences", "array-contains", filter.audience));
    if (filter.kind === "role") constraints.push(where("roles", "array-contains", filter.role));
    if (since !== null) constraints.push(where("sortAt", ">=", Timestamp.fromMillis(since)));
    return constraints;
  }

  async countPublished(filter: ServerFilter, since: number | null): Promise<number> {
    const snap = await withTimeout(getCount(query(collection(this.db, "stories"), ...this.baseConstraints(filter, since))), 12_000, this.timeoutMessage);
    return snap.data().count;
  }

  async listPage(filter: ServerFilter, since: number | null, cursor: Cursor | null): Promise<StoryPage> {
    const constraints: QueryConstraint[] = [where("status", "==", "published")];
    if (filter.kind === "topic") constraints.push(where("primaryTopic", "==", filter.topic));
    if (filter.kind === "audience") constraints.push(where("audiences", "array-contains", filter.audience));
    if (filter.kind === "role") constraints.push(where("roles", "array-contains", filter.role));
    if (since !== null) constraints.push(where("sortAt", ">=", Timestamp.fromMillis(since)));
    constraints.push(orderBy("sortAt", "desc"));
    if (cursor) constraints.push(startAfter(cursor as DocumentSnapshot));
    // One extra document tells us whether another page exists (costs 1 read).
    constraints.push(limit(this.pageSize + 1));

    const snap = await withTimeout(getDocs(query(collection(this.db, "stories"), ...constraints)), 12_000, this.timeoutMessage);
    const docs = snap.docs.slice(0, this.pageSize);
    const stories = docs.map((d) => storyFromData(d.id, d.data())).filter((s): s is Story => s !== null);
    return { stories, cursor: docs.at(-1) ?? null, hasMore: snap.docs.length > this.pageSize };
  }

  async getByIds(ids: string[]): Promise<Map<string, Story>> {
    const out = new Map<string, Story>();
    const unique = [...new Set(ids)].slice(0, LIMITS.maxSavedStories);
    for (const part of chunk(unique, IN_QUERY_MAX)) {
      const q = query(
        collection(this.db, "stories"),
        where("status", "==", "published"),
        where(documentId(), "in", part),
      );
      const snap = await withTimeout(getDocs(q), 12_000, this.timeoutMessage);
      for (const d of snap.docs) {
        const s = storyFromData(d.id, d.data());
        if (s) out.set(s.id, s);
      }
    }
    return out;
  }

  async getPlatformMap(): Promise<PlatformMap | null> {
    const snap = await withTimeout(getDoc(doc(this.db, "meta", "platformMap")), 12_000, this.timeoutMessage);
    if (!snap.exists()) return null;
    const d = snap.data() as Partial<PlatformMap>;
    if (typeof d.generatedAt !== "number" || !d.products || typeof d.products !== "object") return null;
    return { generatedAt: d.generatedAt, storyCount: d.storyCount ?? 0, products: d.products };
  }

  async getRoundups(): Promise<RoundupDoc | null> {
    const snap = await withTimeout(getDoc(doc(this.db, "meta", "roundups")), 12_000, this.timeoutMessage);
    if (!snap.exists()) return null;
    const d = snap.data() as Partial<RoundupDoc>;
    return Array.isArray(d.weeks) ? { generatedAt: d.generatedAt ?? 0, weeks: d.weeks } : null;
  }

  async getTrends(): Promise<TrendsDoc | null> {
    const snap = await withTimeout(getDoc(doc(this.db, "meta", "trends")), 12_000, this.timeoutMessage);
    if (!snap.exists()) return null;
    const d = snap.data() as Partial<TrendsDoc>;
    if (typeof d.generatedAt !== "number" || !d.periods || !Array.isArray(d.daily)) return null;
    return { generatedAt: d.generatedAt, today: d.today ?? 0, periods: d.periods, daily: d.daily };
  }

  async getDigest(): Promise<DigestDoc | null> {
    const snap = await withTimeout(getDoc(doc(this.db, "meta", "digest")), 12_000, this.timeoutMessage);
    if (!snap.exists()) return null;
    const d = snap.data() as Partial<DigestDoc>;
    if (!d.periods || typeof d.generatedAt !== "number") return null;
    return { generatedAt: d.generatedAt, periods: d.periods };
  }

  async getSearchIndex(): Promise<SearchIndexDoc | null> {
    const snap = await withTimeout(getDoc(doc(this.db, "meta", "searchIndex")), 12_000, this.timeoutMessage);
    if (!snap.exists()) return null;
    const d = snap.data() as Partial<SearchIndexDoc> & { shards?: number };
    if (!Array.isArray(d.entries)) return null;
    // Large archives are split across meta/searchIndex-1 … -N (one read each).
    const extra = Math.max(0, Math.min((d.shards ?? 1) - 1, SEARCH_INDEX_MAX_SHARDS - 1));
    const rest = await withTimeout(
      Promise.all(Array.from({ length: extra }, (_, i) => getDoc(doc(this.db, "meta", `searchIndex-${i + 1}`)))),
      20_000,
      this.timeoutMessage,
    );
    const entries = [d.entries, ...rest.map((s) => (s.exists() && Array.isArray(s.get("entries")) ? (s.get("entries") as SearchEntry[]) : []))].flat();
    return { generatedAt: d.generatedAt ?? 0, entries, truncated: d.truncated === true };
  }

  async listByProduct(productId: string, cursor: Cursor | null): Promise<StoryPage> {
    return this.listByProducts([productId], cursor);
  }

  async listByProducts(productIds: string[], cursor: Cursor | null, before?: number): Promise<StoryPage> {
    const ids = [...new Set(productIds)].slice(0, 30);
    const constraints: QueryConstraint[] = [
      where("status", "==", "published"),
      ids.length === 1 ? where("products", "array-contains", ids[0]) : where("products", "array-contains-any", ids),
      ...(before !== undefined ? [where("sortAt", "<", Timestamp.fromMillis(before))] : []),
      orderBy("sortAt", "desc"),
    ];
    if (cursor) constraints.push(startAfter(cursor as DocumentSnapshot));
    constraints.push(limit(PRODUCT_PAGE_SIZE + 1));
    const snap = await withTimeout(getDocs(query(collection(this.db, "stories"), ...constraints)), 12_000, this.timeoutMessage);
    const docs = snap.docs.slice(0, PRODUCT_PAGE_SIZE);
    const stories = docs.map((d) => storyFromData(d.id, d.data())).filter((s): s is Story => s !== null);
    return { stories, cursor: docs.at(-1) ?? null, hasMore: snap.docs.length > PRODUCT_PAGE_SIZE };
  }
}

/** In-memory repository over bundled samples. Same paging semantics as Firestore. */
export class SampleStoryRepository implements StoryRepository {
  readonly label = "Bundled samples";
  private readonly all: Story[];
  private readonly pageSize: number;

  constructor(stories: Story[], pageSize: number = LIMITS.pageSize) {
    this.all = [...stories].sort((a, b) => b.sortAt - a.sortAt);
    this.pageSize = pageSize;
  }

  async listPage(filter: ServerFilter, since: number | null, cursor: Cursor | null): Promise<StoryPage> {
    const matching = this.all.filter(
      (s) =>
        (filter.kind !== "topic" || s.primaryTopic === filter.topic) &&
        (filter.kind !== "audience" || s.audiences.includes(filter.audience)) &&
        (filter.kind !== "role" || storyRoles(s).includes(filter.role)) &&
        (since === null || s.sortAt >= since),
    );
    const start = typeof cursor === "number" ? cursor : 0;
    const stories = matching.slice(start, start + this.pageSize);
    const next = start + stories.length;
    return { stories, cursor: next, hasMore: next < matching.length };
  }

  async countPublished(filter: ServerFilter, since: number | null): Promise<number> {
    return this.all.filter(
      (s) =>
        (filter.kind !== "topic" || s.primaryTopic === filter.topic) &&
        (filter.kind !== "audience" || s.audiences.includes(filter.audience)) &&
        (filter.kind !== "role" || storyRoles(s).includes(filter.role)) &&
        (since === null || s.sortAt >= since),
    ).length;
  }

  async getByIds(ids: string[]): Promise<Map<string, Story>> {
    const want = new Set(ids);
    return new Map(this.all.filter((s) => want.has(s.id)).map((s) => [s.id, s]));
  }

  async getPlatformMap(): Promise<PlatformMap | null> {
    return buildPlatformMap(this.all);
  }

  async getSearchIndex(): Promise<SearchIndexDoc | null> {
    return buildSearchIndex(this.all);
  }

  async getDigest(): Promise<DigestDoc | null> {
    return buildDigest(this.all);
  }

  async getRoundups(): Promise<RoundupDoc | null> {
    return buildRoundups(this.all);
  }

  async getTrends(): Promise<TrendsDoc | null> {
    return sampleTrends(this.all, Date.now());
  }

  async listByProduct(productId: string, cursor: Cursor | null): Promise<StoryPage> {
    return this.listByProducts([productId], cursor);
  }

  async listByProducts(productIds: string[], cursor: Cursor | null, before?: number): Promise<StoryPage> {
    const want = new Set(productIds);
    const matching = this.all.filter((s) => s.products.some((p) => want.has(p)) && (before === undefined || s.sortAt < before));
    const start = typeof cursor === "number" ? cursor : 0;
    const stories = matching.slice(start, start + PRODUCT_PAGE_SIZE);
    const next = start + stories.length;
    return { stories, cursor: next, hasMore: next < matching.length };
  }
}
